import { memo } from 'react';

/**
 * BuildingSVGs - Custom SVG graphics for all 100 TypeTown buildings
 * Each building has a unique, blocky, kid-friendly design
 * Organized by Stage (1-5) with 20 buildings per stage
 */

// Utility: Create a building wrapper with consistent sizing and effects
const BuildingWrapper = memo(({ children, scale = 1, isUserBuilding = false, size = 350 }) => {
  const scaledSize = Math.round(size * scale);
  return (
    <div 
      className={isUserBuilding ? 'hover:brightness-110 transition-all cursor-pointer' : ''}
      style={{ transform: `scale(${scale})` }}
    >
      <svg width={scaledSize} height={scaledSize} viewBox="0 0 80 80">
        {/* Ground shadow */}
        <ellipse cx="40" cy="76" rx="30" ry="4" fill="rgba(0,0,0,0.2)" />
        {children}
      </svg>
    </div>
  );
});

// ==================== STAGE 1: CAMPSITE (Levels 1-20) ====================

// 1. Tent
export const TentSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <polygon points="40,10 10,70 70,70" fill="#FF7043" stroke="#E64A19" strokeWidth="2" />
    <polygon points="40,10 10,70 40,70" fill="#FF5722" />
    <polygon points="40,35 28,70 52,70" fill="#5D4037" />
    <line x1="40" y1="5" x2="40" y2="15" stroke="#8D6E63" strokeWidth="3" />
  </BuildingWrapper>
));

// 2. Campfire
export const CampfireSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="68" rx="25" ry="6" fill="#795548" />
    <rect x="15" y="55" width="8" height="20" fill="#8D6E63" transform="rotate(-15, 19, 65)" />
    <rect x="57" y="55" width="8" height="20" fill="#8D6E63" transform="rotate(15, 61, 65)" />
    <rect x="33" y="50" width="6" height="22" fill="#6D4C41" />
    <rect x="41" y="52" width="6" height="20" fill="#6D4C41" />
    <ellipse cx="40" cy="40" rx="15" ry="25" fill="#FF6F00" />
    <ellipse cx="40" cy="35" rx="10" ry="18" fill="#FFAB00" />
    <ellipse cx="40" cy="28" rx="5" ry="12" fill="#FFEA00" />
  </BuildingWrapper>
));

// 3. Pine Tree
export const PineTreeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="35" y="55" width="10" height="20" fill="#5D4037" />
    <polygon points="40,8 18,35 62,35" fill="#2E7D32" />
    <polygon points="40,20 15,50 65,50" fill="#388E3C" />
    <polygon points="40,35 12,65 68,65" fill="#43A047" />
  </BuildingWrapper>
));

// 4. Oak Tree
export const OakTreeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="34" y="45" width="12" height="28" fill="#6D4C41" />
    <circle cx="40" cy="32" r="25" fill="#4CAF50" />
    <circle cx="28" cy="38" r="12" fill="#66BB6A" />
    <circle cx="52" cy="38" r="12" fill="#66BB6A" />
    <circle cx="40" cy="20" r="15" fill="#81C784" />
  </BuildingWrapper>
));

// 5. Bush
export const BushSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="55" rx="30" ry="18" fill="#558B2F" />
    <ellipse cx="28" cy="48" rx="15" ry="12" fill="#689F38" />
    <ellipse cx="52" cy="50" rx="14" ry="10" fill="#7CB342" />
    <ellipse cx="40" cy="42" rx="12" ry="10" fill="#8BC34A" />
  </BuildingWrapper>
));

// 6. Flower Bed
export const FlowerBedSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="55" width="60" height="15" rx="3" fill="#5D4037" />
    <ellipse cx="20" cy="55" rx="8" ry="5" fill="#4CAF50" />
    <ellipse cx="40" cy="55" rx="8" ry="5" fill="#4CAF50" />
    <ellipse cx="60" cy="55" rx="8" ry="5" fill="#4CAF50" />
    <circle cx="20" cy="42" r="8" fill="#E91E63" />
    <circle cx="40" cy="38" r="10" fill="#FF9800" />
    <circle cx="60" cy="44" r="7" fill="#9C27B0" />
    <circle cx="20" cy="42" r="3" fill="#FFC107" />
    <circle cx="40" cy="38" r="4" fill="#FFC107" />
    <circle cx="60" cy="44" r="2" fill="#FFC107" />
  </BuildingWrapper>
));

// 7. Rock
export const RockSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <polygon points="15,70 8,50 20,35 40,30 55,35 70,45 65,70" fill="#78909C" />
    <polygon points="20,60 15,45 30,38 45,40 50,55" fill="#90A4AE" />
    <polygon points="50,65 55,50 62,55 58,68" fill="#607D8B" />
  </BuildingWrapper>
));

// 8. Log Bench
export const LogBenchSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="15" cy="60" rx="8" ry="12" fill="#5D4037" />
    <ellipse cx="65" cy="60" rx="8" ry="12" fill="#5D4037" />
    <rect x="10" y="40" width="60" height="14" rx="7" fill="#8D6E63" />
    <ellipse cx="15" cy="60" rx="6" ry="8" fill="#6D4C41" />
    <ellipse cx="65" cy="60" rx="6" ry="8" fill="#6D4C41" />
  </BuildingWrapper>
));

// 9. Lantern
export const LanternSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="35" y="55" width="10" height="18" fill="#5D4037" />
    <rect x="30" y="25" width="20" height="32" rx="2" fill="#FF5722" />
    <rect x="33" y="30" width="14" height="22" fill="#FFEB3B" />
    <rect x="28" y="22" width="24" height="5" fill="#D32F2F" />
    <rect x="28" y="55" width="24" height="4" fill="#D32F2F" />
    <circle cx="40" cy="15" r="5" fill="#FFD54F" />
  </BuildingWrapper>
));

// 10. Toadstool
export const ToadstoolSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="34" y="50" width="12" height="22" fill="#ECEFF1" />
    <ellipse cx="40" cy="40" rx="25" ry="18" fill="#F44336" />
    <circle cx="30" cy="35" r="5" fill="#FFEBEE" />
    <circle cx="50" cy="38" r="4" fill="#FFEBEE" />
    <circle cx="40" cy="30" r="6" fill="#FFEBEE" />
    <circle cx="55" cy="45" r="3" fill="#FFEBEE" />
    <ellipse cx="40" cy="70" rx="8" ry="3" fill="#D7CCC8" />
  </BuildingWrapper>
));

// 11. Bird Box
export const BirdBoxSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="36" y="45" width="8" height="28" fill="#5D4037" />
    <rect x="28" y="22" width="24" height="28" fill="#8D6E63" />
    <polygon points="25,25 40,10 55,25" fill="#6D4C41" />
    <circle cx="40" cy="38" r="6" fill="#3E2723" />
    <rect x="38" y="48" width="4" height="6" fill="#FFCC80" />
  </BuildingWrapper>
));

// 12. Beehive
export const BeehiveSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="36" y="60" width="8" height="14" fill="#5D4037" />
    <ellipse cx="40" cy="52" rx="18" ry="8" fill="#FFC107" />
    <ellipse cx="40" cy="42" rx="15" ry="7" fill="#FFD54F" />
    <ellipse cx="40" cy="33" rx="12" ry="6" fill="#FFECB3" />
    <ellipse cx="40" cy="26" rx="8" ry="5" fill="#FFF8E1" />
    <circle cx="55" cy="35" r="4" fill="#212121" />
    <ellipse cx="58" cy="35" rx="3" ry="2" fill="#90A4AE" />
    <circle cx="62" cy="28" r="3" fill="#212121" />
  </BuildingWrapper>
));

// 13. Pond
export const PondSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="55" rx="32" ry="18" fill="#0288D1" />
    <ellipse cx="40" cy="52" rx="28" ry="14" fill="#03A9F4" />
    <ellipse cx="35" cy="50" rx="10" ry="5" fill="#4FC3F7" opacity="0.6" />
    <ellipse cx="25" cy="55" rx="6" ry="8" fill="#4CAF50" />
    <ellipse cx="58" cy="52" rx="5" ry="7" fill="#66BB6A" />
  </BuildingWrapper>
));

// 14. Wooden Sign
export const WoodenSignSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="36" y="40" width="8" height="32" fill="#5D4037" />
    <rect x="15" y="20" width="50" height="25" rx="2" fill="#8D6E63" />
    <rect x="18" y="24" width="44" height="17" fill="#A1887F" />
    <line x1="25" y1="30" x2="55" y2="30" stroke="#6D4C41" strokeWidth="2" />
    <line x1="30" y1="36" x2="50" y2="36" stroke="#6D4C41" strokeWidth="2" />
  </BuildingWrapper>
));

// 15. Hammock
export const HammockSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="25" width="6" height="45" fill="#5D4037" />
    <rect x="66" y="25" width="6" height="45" fill="#5D4037" />
    <path d="M 12 35 Q 40 60 68 35" stroke="#FF7043" strokeWidth="6" fill="none" />
    <path d="M 12 38 Q 40 63 68 38" stroke="#FF5722" strokeWidth="4" fill="none" />
    <line x1="12" y1="35" x2="8" y2="28" stroke="#8D6E63" strokeWidth="2" />
    <line x1="68" y1="35" x2="72" y2="28" stroke="#8D6E63" strokeWidth="2" />
  </BuildingWrapper>
));

// 16. Fire Ring
export const FireRingSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="58" rx="28" ry="14" fill="#616161" />
    <ellipse cx="40" cy="55" rx="22" ry="10" fill="#424242" />
    <ellipse cx="40" cy="52" rx="16" ry="7" fill="#757575" />
    <ellipse cx="40" cy="45" rx="10" ry="15" fill="#FF6F00" />
    <ellipse cx="40" cy="40" rx="6" ry="10" fill="#FFAB00" />
  </BuildingWrapper>
));

// 17. Picnic Table
export const PicnicTableSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="12" y="42" width="56" height="6" fill="#8D6E63" />
    <rect x="18" y="55" width="44" height="5" fill="#6D4C41" />
    <rect x="16" y="47" width="6" height="22" fill="#5D4037" transform="rotate(-10, 19, 58)" />
    <rect x="58" y="47" width="6" height="22" fill="#5D4037" transform="rotate(10, 61, 58)" />
    <rect x="35" y="47" width="10" height="20" fill="#5D4037" />
  </BuildingWrapper>
));

// 18. Cool Box
export const CoolBoxSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="18" y="35" width="44" height="35" rx="3" fill="#42A5F5" />
    <rect x="18" y="35" width="44" height="8" fill="#1E88E5" />
    <rect x="32" y="38" width="16" height="3" rx="1" fill="#90CAF9" />
    <rect x="20" y="45" width="40" height="2" fill="#1976D2" />
    <rect x="16" y="68" width="10" height="4" fill="#1565C0" />
    <rect x="54" y="68" width="10" height="4" fill="#1565C0" />
  </BuildingWrapper>
));

// 19. Fishing Rod
export const FishingRodSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <line x1="25" y1="70" x2="55" y2="15" stroke="#8D6E63" strokeWidth="4" />
    <line x1="55" y1="15" x2="55" y2="55" stroke="#90A4AE" strokeWidth="1" />
    <ellipse cx="55" cy="58" rx="4" ry="6" fill="#FF5722" />
    <circle cx="25" cy="70" r="5" fill="#5D4037" />
    <ellipse cx="30" cy="55" rx="8" ry="4" fill="#6D4C41" />
  </BuildingWrapper>
));

// 20. Canoe
export const CanoeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="55" rx="35" ry="10" fill="#8D6E63" />
    <ellipse cx="40" cy="52" rx="30" ry="6" fill="#A1887F" />
    <rect x="25" y="48" width="30" height="8" fill="#6D4C41" />
    <line x1="20" y1="30" x2="60" y2="50" stroke="#5D4037" strokeWidth="3" />
    <ellipse cx="18" cy="28" rx="6" ry="12" fill="#5D4037" />
    <ellipse cx="62" cy="52" rx="6" ry="10" fill="#5D4037" />
  </BuildingWrapper>
));

// ==================== STAGE 2: HAMLET (Levels 21-40) ====================

// 21. Cottage
export const CottageSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="15" y="40" width="50" height="32" fill="#FFCC80" stroke="#E65100" strokeWidth="2" />
    <polygon points="40,12 8,42 72,42" fill="#D84315" />
    <rect x="32" y="50" width="16" height="22" fill="#5D4037" />
    <rect x="50" y="48" width="10" height="10" fill="#81D4FA" stroke="#5D4037" />
    <circle cx="45" cy="62" r="2" fill="#FFC107" />
  </BuildingWrapper>
));

// 22. Well
export const WellSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="62" rx="22" ry="10" fill="#78909C" />
    <ellipse cx="40" cy="58" rx="18" ry="7" fill="#0288D1" />
    <rect x="16" y="35" width="6" height="35" fill="#6D4C41" />
    <rect x="58" y="35" width="6" height="35" fill="#6D4C41" />
    <rect x="14" y="30" width="52" height="8" fill="#8D6E63" />
    <rect x="36" y="15" width="8" height="20" fill="#5D4037" />
    <ellipse cx="40" cy="25" rx="10" ry="6" fill="#90A4AE" />
  </BuildingWrapper>
));

// 23. Veggie Patch
export const VeggiePatchSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="50" width="60" height="20" fill="#5D4037" />
    <rect x="12" y="52" width="12" height="16" fill="#6D4C41" />
    <rect x="26" y="52" width="12" height="16" fill="#6D4C41" />
    <rect x="40" y="52" width="12" height="16" fill="#6D4C41" />
    <rect x="54" y="52" width="14" height="16" fill="#6D4C41" />
    <polygon points="18,52 18,35 20,35 20,52" fill="#4CAF50" />
    <ellipse cx="18" cy="32" rx="6" ry="4" fill="#66BB6A" />
    <polygon points="32,52 32,38 34,38 34,52" fill="#4CAF50" />
    <circle cx="33" cy="35" r="5" fill="#F44336" />
    <polygon points="46,52 46,40 48,40 48,52" fill="#FF9800" />
    <polygon points="60,52 60,38 62,38 62,52" fill="#8BC34A" />
  </BuildingWrapper>
));

// 24. Wooden Fence
export const WoodenFenceSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="35" width="8" height="35" fill="#8D6E63" />
    <polygon points="12,35 8,25 16,25" fill="#8D6E63" />
    <rect x="24" y="35" width="8" height="35" fill="#8D6E63" />
    <polygon points="28,35 24,25 32,25" fill="#8D6E63" />
    <rect x="40" y="35" width="8" height="35" fill="#8D6E63" />
    <polygon points="44,35 40,25 48,25" fill="#8D6E63" />
    <rect x="56" y="35" width="8" height="35" fill="#8D6E63" />
    <polygon points="60,35 56,25 64,25" fill="#8D6E63" />
    <rect x="5" y="42" width="65" height="6" fill="#6D4C41" />
    <rect x="5" y="58" width="65" height="6" fill="#6D4C41" />
  </BuildingWrapper>
));

// 25. Cobble Path
export const CobblePathSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="45" width="60" height="25" rx="3" fill="#9E9E9E" />
    <ellipse cx="20" cy="52" rx="8" ry="6" fill="#757575" />
    <ellipse cx="38" cy="55" rx="10" ry="7" fill="#616161" />
    <ellipse cx="55" cy="50" rx="9" ry="6" fill="#757575" />
    <ellipse cx="28" cy="62" rx="7" ry="5" fill="#616161" />
    <ellipse cx="50" cy="64" rx="8" ry="5" fill="#757575" />
  </BuildingWrapper>
));

// 26. Chicken Coop
export const ChickenCoopSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="12" y="40" width="56" height="30" fill="#8D6E63" />
    <polygon points="40,18 8,42 72,42" fill="#D84315" />
    <rect x="20" y="50" width="12" height="18" fill="#5D4037" />
    <rect x="22" y="52" width="8" height="2" fill="#A1887F" />
    <rect x="22" y="56" width="8" height="2" fill="#A1887F" />
    <rect x="48" y="48" width="14" height="10" fill="#FFEB3B" stroke="#5D4037" />
    <circle cx="68" cy="58" r="6" fill="#ECEFF1" />
    <polygon points="68,55 70,52 72,55" fill="#FF5722" />
  </BuildingWrapper>
));

// 27. Windmill
export const WindmillSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <polygon points="30,72 25,35 55,35 50,72" fill="#ECEFF1" />
    <rect x="35" y="55" width="10" height="17" fill="#5D4037" />
    <polygon points="40,38 38,8 42,8" fill="#78909C" />
    <polygon points="40,38 70,32 68,36" fill="#78909C" />
    <polygon points="40,38 12,44 14,40" fill="#78909C" />
    <polygon points="40,38 45,68 41,66" fill="#78909C" />
    <circle cx="40" cy="38" r="4" fill="#455A64" />
  </BuildingWrapper>
));

// 28. Hay Bale
export const HayBaleSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="58" rx="28" ry="14" fill="#FFC107" />
    <ellipse cx="40" cy="55" rx="28" ry="14" fill="#FFD54F" />
    <ellipse cx="40" cy="55" rx="22" ry="10" fill="#FFECB3" />
    <line x1="20" y1="52" x2="20" y2="60" stroke="#F9A825" strokeWidth="2" />
    <line x1="35" y1="50" x2="35" y2="62" stroke="#F9A825" strokeWidth="2" />
    <line x1="50" y1="50" x2="50" y2="62" stroke="#F9A825" strokeWidth="2" />
    <line x1="60" y1="52" x2="60" y2="60" stroke="#F9A825" strokeWidth="2" />
  </BuildingWrapper>
));

// 29. Scarecrow
export const ScarecrowSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="37" y="35" width="6" height="38" fill="#6D4C41" />
    <rect x="20" y="40" width="40" height="6" fill="#6D4C41" />
    <circle cx="40" cy="25" r="12" fill="#FFCC80" />
    <circle cx="35" cy="23" r="2" fill="#3E2723" />
    <circle cx="45" cy="23" r="2" fill="#3E2723" />
    <path d="M 35 30 Q 40 34 45 30" stroke="#5D4037" strokeWidth="2" fill="none" />
    <polygon points="40,13 35,5 45,5" fill="#FFC107" />
    <rect x="22" y="45" width="16" height="18" fill="#8D6E63" />
    <rect x="42" y="45" width="16" height="18" fill="#8D6E63" />
  </BuildingWrapper>
));

// 30. Apple Tree
export const AppleTreeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="35" y="50" width="10" height="22" fill="#6D4C41" />
    <circle cx="40" cy="35" r="22" fill="#4CAF50" />
    <circle cx="30" cy="30" r="5" fill="#F44336" />
    <circle cx="50" cy="32" r="4" fill="#F44336" />
    <circle cx="38" cy="42" r="5" fill="#F44336" />
    <circle cx="48" cy="25" r="4" fill="#F44336" />
  </BuildingWrapper>
));

// 31. Sunflowers
export const SunflowersSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <line x1="25" y1="72" x2="25" y2="35" stroke="#4CAF50" strokeWidth="4" />
    <line x1="55" y1="72" x2="55" y2="42" stroke="#4CAF50" strokeWidth="4" />
    <circle cx="25" cy="28" r="14" fill="#FFC107" />
    <circle cx="25" cy="28" r="8" fill="#5D4037" />
    <circle cx="55" cy="36" r="12" fill="#FFC107" />
    <circle cx="55" cy="36" r="6" fill="#5D4037" />
    <ellipse cx="18" cy="50" rx="8" ry="4" fill="#66BB6A" />
    <ellipse cx="60" cy="55" rx="7" ry="3" fill="#66BB6A" />
  </BuildingWrapper>
));

// 32. Stone Wall
export const StoneWallSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="40" width="64" height="30" fill="#78909C" />
    <rect x="10" y="42" width="18" height="12" fill="#90A4AE" stroke="#607D8B" />
    <rect x="30" y="42" width="20" height="12" fill="#B0BEC5" stroke="#607D8B" />
    <rect x="52" y="42" width="18" height="12" fill="#90A4AE" stroke="#607D8B" />
    <rect x="10" y="56" width="22" height="12" fill="#B0BEC5" stroke="#607D8B" />
    <rect x="34" y="56" width="16" height="12" fill="#90A4AE" stroke="#607D8B" />
    <rect x="52" y="56" width="18" height="12" fill="#B0BEC5" stroke="#607D8B" />
  </BuildingWrapper>
));

// 33. Trolley
export const TrolleySVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="18" y="30" width="40" height="30" fill="#8D6E63" />
    <rect x="16" y="28" width="44" height="4" fill="#6D4C41" />
    <circle cx="22" cy="68" r="6" fill="#424242" />
    <circle cx="54" cy="68" r="6" fill="#424242" />
    <line x1="20" y1="25" x2="10" y2="15" stroke="#5D4037" strokeWidth="3" />
    <line x1="56" y1="25" x2="66" y2="15" stroke="#5D4037" strokeWidth="3" />
    <rect x="22" y="35" width="32" height="6" fill="#FFC107" />
    <rect x="22" y="45" width="32" height="6" fill="#FF9800" />
  </BuildingWrapper>
));

// 34. Barn
export const BarnSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="60" height="37" fill="#C62828" />
    <polygon points="40,10 5,38 75,38" fill="#B71C1C" />
    <rect x="30" y="45" width="20" height="27" fill="#5D4037" />
    <line x1="40" y1="45" x2="40" y2="72" stroke="#3E2723" strokeWidth="2" />
    <rect x="15" y="42" width="10" height="8" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="55" y="42" width="10" height="8" fill="#FFEB3B" stroke="#5D4037" />
    <circle cx="40" cy="20" r="6" fill="#FFEB3B" stroke="#5D4037" />
  </BuildingWrapper>
));

// 35. Sheep Pen
export const SheepPenSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="55" width="60" height="15" fill="#8D6E63" />
    <rect x="10" y="40" width="4" height="30" fill="#6D4C41" />
    <rect x="66" y="40" width="4" height="30" fill="#6D4C41" />
    <rect x="10" y="40" width="60" height="4" fill="#6D4C41" />
    <ellipse cx="28" cy="52" rx="10" ry="8" fill="#ECEFF1" />
    <circle cx="22" cy="48" r="5" fill="#ECEFF1" />
    <ellipse cx="50" cy="54" rx="9" ry="7" fill="#ECEFF1" />
    <circle cx="45" cy="50" r="4" fill="#ECEFF1" />
  </BuildingWrapper>
));

// 36. Horse Stable
export const HorseStableSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="60" height="37" fill="#8D6E63" />
    <polygon points="40,15 5,38 75,38" fill="#6D4C41" />
    <rect x="20" y="40" width="18" height="32" fill="#5D4037" />
    <rect x="42" y="40" width="18" height="32" fill="#5D4037" />
    <rect x="22" y="42" width="14" height="8" fill="#4E342E" />
    <rect x="44" y="42" width="14" height="8" fill="#4E342E" />
    <ellipse cx="29" cy="60" rx="6" ry="10" fill="#8D6E63" />
    <ellipse cx="51" cy="58" rx="5" ry="8" fill="#5D4037" />
  </BuildingWrapper>
));

// 37. Post Box
export const PostBoxSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="30" y="30" width="20" height="40" rx="2" fill="#F44336" />
    <rect x="28" y="25" width="24" height="8" rx="4" fill="#D32F2F" />
    <rect x="33" y="40" width="14" height="4" fill="#212121" />
    <rect x="35" y="52" width="10" height="8" fill="#FFD54F" />
    <text x="40" y="60" textAnchor="middle" fontSize="6" fill="#5D4037">POST</text>
  </BuildingWrapper>
));

// 38. Signpost
export const SignpostSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="37" y="30" width="6" height="42" fill="#6D4C41" />
    <polygon points="42,25 70,20 70,32 42,35" fill="#8D6E63" />
    <polygon points="38,35 10,30 10,42 38,45" fill="#A1887F" />
    <polygon points="42,45 65,42 65,52 42,55" fill="#8D6E63" />
  </BuildingWrapper>
));

// 39. Bread Oven
export const BreadOvenSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="60" rx="28" ry="12" fill="#795548" />
    <ellipse cx="40" cy="45" rx="24" ry="20" fill="#A1887F" />
    <ellipse cx="40" cy="42" rx="20" ry="16" fill="#BCAAA4" />
    <ellipse cx="40" cy="55" rx="10" ry="8" fill="#3E2723" />
    <ellipse cx="40" cy="30" rx="5" ry="8" fill="#FF6F00" opacity="0.7" />
    <rect x="32" y="15" width="6" height="15" fill="#757575" />
    <ellipse cx="35" cy="12" rx="6" ry="4" fill="#9E9E9E" opacity="0.6" />
  </BuildingWrapper>
));

// 40. Water Trough
export const WaterTroughSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="45" width="60" height="22" rx="2" fill="#8D6E63" />
    <rect x="14" y="48" width="52" height="16" fill="#0288D1" />
    <rect x="16" y="50" width="48" height="12" fill="#03A9F4" />
    <ellipse cx="30" cy="54" rx="8" ry="3" fill="#4FC3F7" opacity="0.5" />
    <rect x="8" y="65" width="8" height="8" fill="#6D4C41" />
    <rect x="64" y="65" width="8" height="8" fill="#6D4C41" />
  </BuildingWrapper>
));

// ==================== STAGE 3: VILLAGE (Levels 41-60) ====================

// 41. House
export const HouseSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="12" y="38" width="56" height="34" fill="#FFCC80" stroke="#E65100" strokeWidth="2" />
    <polygon points="40,10 5,40 75,40" fill="#B71C1C" />
    <rect x="62" y="15" width="8" height="20" fill="#795548" />
    <rect x="34" y="50" width="12" height="22" fill="#5D4037" />
    <circle cx="43" cy="62" r="2" fill="#FFC107" />
    <rect x="16" y="45" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="52" y="45" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
  </BuildingWrapper>
));

// 42. Shop
export const ShopSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="60" height="37" fill="#FFF9C4" stroke="#F9A825" strokeWidth="2" />
    <rect x="8" y="28" width="64" height="10" fill="#FF8F00" />
    <polygon points="8,28 12,20 68,20 72,28" fill="#FFB300" />
    <rect x="15" y="40" width="22" height="30" fill="#81D4FA" stroke="#5D4037" />
    <rect x="43" y="40" width="22" height="30" fill="#81D4FA" stroke="#5D4037" />
    <rect x="15" y="72" width="22" height="3" fill="#4CAF50" />
    <rect x="43" y="72" width="22" height="3" fill="#4CAF50" />
  </BuildingWrapper>
));

// 43. Chapel
export const ChapelSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="18" y="35" width="44" height="37" fill="#ECEFF1" stroke="#90A4AE" strokeWidth="2" />
    <polygon points="40,8 12,38 68,38" fill="#607D8B" />
    <rect x="37" y="3" width="6" height="10" fill="#FFC107" />
    <line x1="34" y1="8" x2="46" y2="8" stroke="#FFC107" strokeWidth="2" />
    <rect x="34" y="48" width="12" height="24" rx="6" fill="#5D4037" />
    <circle cx="40" cy="28" r="8" fill="#81D4FA" stroke="#5D4037" />
  </BuildingWrapper>
));

// 44. Village Green
export const VillageGreenSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="60" rx="35" ry="14" fill="#66BB6A" />
    <ellipse cx="40" cy="58" rx="30" ry="10" fill="#81C784" />
    <rect x="37" y="35" width="6" height="25" fill="#6D4C41" />
    <circle cx="40" cy="28" r="14" fill="#4CAF50" />
    <circle cx="35" cy="25" r="8" fill="#66BB6A" />
    <circle cx="48" cy="30" r="6" fill="#81C784" />
    <rect x="55" y="52" width="12" height="6" fill="#8D6E63" />
  </BuildingWrapper>
));

// 45. Paved Road
export const PavedRoadSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="40" width="70" height="30" fill="#616161" />
    <rect x="8" y="53" width="64" height="4" fill="#FFEB3B" />
    <rect x="8" y="53" width="10" height="4" fill="#FFEB3B" />
    <rect x="25" y="53" width="10" height="4" fill="#FFEB3B" />
    <rect x="42" y="53" width="10" height="4" fill="#FFEB3B" />
    <rect x="59" y="53" width="10" height="4" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 46. Street Lamp
export const StreetLampSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="37" y="35" width="6" height="38" fill="#37474F" />
    <rect x="35" y="70" width="10" height="4" fill="#455A64" />
    <rect x="28" y="25" width="24" height="14" rx="2" fill="#455A64" />
    <rect x="30" y="28" width="20" height="8" fill="#FFEB3B" />
    <ellipse cx="40" cy="45" rx="15" ry="8" fill="#FFEB3B" opacity="0.3" />
  </BuildingWrapper>
));

// 47. Park Bench (village style)
export const ParkBenchVillageSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="45" width="60" height="8" fill="#4CAF50" />
    <rect x="10" y="35" width="60" height="12" fill="#388E3C" />
    <rect x="12" y="52" width="8" height="16" fill="#2E7D32" />
    <rect x="60" y="52" width="8" height="16" fill="#2E7D32" />
    <rect x="14" y="38" width="4" height="28" fill="#1B5E20" />
    <rect x="62" y="38" width="4" height="28" fill="#1B5E20" />
  </BuildingWrapper>
));

// 48. Notice Board
export const NoticeBoardSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="15" y="50" width="6" height="22" fill="#6D4C41" />
    <rect x="59" y="50" width="6" height="22" fill="#6D4C41" />
    <rect x="12" y="20" width="56" height="35" fill="#8D6E63" />
    <rect x="16" y="24" width="48" height="27" fill="#D7CCC8" />
    <rect x="20" y="28" width="15" height="10" fill="#FFEB3B" />
    <rect x="38" y="28" width="12" height="8" fill="#FF7043" />
    <rect x="24" y="42" width="20" height="6" fill="#81D4FA" />
  </BuildingWrapper>
));

// 49. Bakery
export const BakerySVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="32" width="60" height="40" fill="#FFF8E1" stroke="#F9A825" strokeWidth="2" />
    <polygon points="40,10 5,35 75,35" fill="#8D6E63" />
    <rect x="30" y="50" width="20" height="22" fill="#5D4037" />
    <rect x="15" y="40" width="12" height="15" fill="#81D4FA" stroke="#5D4037" />
    <rect x="53" y="40" width="12" height="15" fill="#81D4FA" stroke="#5D4037" />
    <ellipse cx="40" cy="22" rx="10" ry="6" fill="#FFB74D" />
    <text x="40" y="68" textAnchor="middle" fontSize="5" fill="#5D4037">BAKERY</text>
  </BuildingWrapper>
));

// 50. Blacksmith
export const BlacksmithSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="60" height="37" fill="#5D4037" stroke="#3E2723" strokeWidth="2" />
    <polygon points="40,15 5,38 75,38" fill="#3E2723" />
    <rect x="25" y="45" width="30" height="27" fill="#2E2E2E" />
    <ellipse cx="40" cy="55" rx="12" ry="8" fill="#FF6F00" />
    <ellipse cx="40" cy="52" rx="8" ry="5" fill="#FFAB00" />
    <rect x="60" y="40" width="6" height="25" fill="#78909C" />
    <polygon points="63,35 60,40 66,40" fill="#90A4AE" />
  </BuildingWrapper>
));

// 51. Inn
export const InnSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="32" width="64" height="40" fill="#BCAAA4" stroke="#8D6E63" strokeWidth="2" />
    <polygon points="40,8 2,35 78,35" fill="#6D4C41" />
    <rect x="32" y="48" width="16" height="24" fill="#5D4037" />
    <rect x="12" y="40" width="14" height="12" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="54" y="40" width="14" height="12" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="12" y="56" width="14" height="12" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="54" y="56" width="14" height="12" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="55" y="15" width="8" height="14" fill="#757575" />
  </BuildingWrapper>
));

// 52. Fountain
export const FountainSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="65" rx="30" ry="10" fill="#78909C" />
    <ellipse cx="40" cy="62" rx="26" ry="7" fill="#0288D1" />
    <ellipse cx="40" cy="60" rx="22" ry="5" fill="#03A9F4" />
    <rect x="36" y="40" width="8" height="22" fill="#90A4AE" />
    <ellipse cx="40" cy="42" rx="10" ry="4" fill="#78909C" />
    <ellipse cx="40" cy="30" rx="4" ry="15" fill="#4FC3F7" opacity="0.6" />
    <circle cx="40" cy="18" r="3" fill="#4FC3F7" opacity="0.4" />
  </BuildingWrapper>
));

// 53. Clock Tower
export const ClockTowerSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="25" y="30" width="30" height="42" fill="#8D6E63" />
    <polygon points="40,8 20,32 60,32" fill="#6D4C41" />
    <circle cx="40" cy="45" r="12" fill="#ECEFF1" stroke="#5D4037" strokeWidth="2" />
    <line x1="40" y1="45" x2="40" y2="38" stroke="#212121" strokeWidth="2" />
    <line x1="40" y1="45" x2="48" y2="48" stroke="#212121" strokeWidth="2" />
    <circle cx="40" cy="45" r="2" fill="#212121" />
    <rect x="35" y="60" width="10" height="12" fill="#5D4037" />
  </BuildingWrapper>
));

// 54. Flower Stall
export const FlowerStallSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="12" y="45" width="56" height="25" fill="#8D6E63" />
    <rect x="8" y="35" width="64" height="12" fill="#FF7043" />
    <polygon points="8,35 12,25 68,25 72,35" fill="#FF5722" />
    <circle cx="22" cy="50" r="6" fill="#E91E63" />
    <circle cx="36" cy="48" r="7" fill="#FFEB3B" />
    <circle cx="50" cy="50" r="6" fill="#9C27B0" />
    <circle cx="62" cy="48" r="5" fill="#F44336" />
    <rect x="20" y="55" width="8" height="12" fill="#4CAF50" />
    <rect x="36" y="55" width="8" height="12" fill="#4CAF50" />
    <rect x="52" y="55" width="8" height="12" fill="#4CAF50" />
  </BuildingWrapper>
));

// 55. Bandstand
export const BandstandSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="68" rx="30" ry="6" fill="#8D6E63" />
    <polygon points="40,10 8,35 72,35" fill="#B71C1C" />
    <rect x="12" y="35" width="4" height="35" fill="#ECEFF1" />
    <rect x="64" y="35" width="4" height="35" fill="#ECEFF1" />
    <rect x="36" y="35" width="8" height="35" fill="#ECEFF1" />
    <ellipse cx="40" cy="70" rx="26" ry="4" fill="#D7CCC8" />
    <rect x="38" y="8" width="4" height="8" fill="#FFC107" />
  </BuildingWrapper>
));

// 56. Playground
export const PlaygroundSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="68" rx="35" ry="8" fill="#FFCC80" />
    <rect x="55" y="35" width="4" height="35" fill="#F44336" />
    <rect x="68" y="35" width="4" height="35" fill="#F44336" />
    <rect x="53" y="32" width="22" height="5" fill="#F44336" />
    <rect x="58" y="55" width="12" height="3" fill="#2196F3" />
    <line x1="64" y1="38" x2="64" y2="55" stroke="#9E9E9E" strokeWidth="1" />
    <rect x="12" y="50" width="30" height="18" rx="3" fill="#4CAF50" />
    <rect x="15" y="53" width="24" height="12" fill="#81C784" />
  </BuildingWrapper>
));

// 57. Statue
export const StatueSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="25" y="60" width="30" height="12" fill="#78909C" />
    <rect x="30" y="52" width="20" height="10" fill="#90A4AE" />
    <ellipse cx="40" cy="40" rx="10" ry="14" fill="#B0BEC5" />
    <circle cx="40" cy="22" r="10" fill="#B0BEC5" />
    <rect x="25" y="35" width="6" height="15" fill="#B0BEC5" transform="rotate(-30, 28, 42)" />
    <rect x="49" y="35" width="6" height="15" fill="#B0BEC5" transform="rotate(30, 52, 42)" />
  </BuildingWrapper>
));

// 58. Bridge
export const BridgeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="55" width="70" height="15" fill="#0288D1" />
    <path d="M 5 50 Q 40 30 75 50" stroke="#8D6E63" strokeWidth="10" fill="none" />
    <path d="M 5 50 Q 40 35 75 50" stroke="#A1887F" strokeWidth="6" fill="none" />
    <rect x="10" y="40" width="6" height="15" fill="#6D4C41" />
    <rect x="64" y="40" width="6" height="15" fill="#6D4C41" />
  </BuildingWrapper>
));

// 59. Fishing Pier
export const FishingPierSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="55" width="70" height="20" fill="#0288D1" />
    <rect x="20" y="45" width="45" height="8" fill="#8D6E63" />
    <rect x="22" y="52" width="4" height="18" fill="#6D4C41" />
    <rect x="40" y="52" width="4" height="18" fill="#6D4C41" />
    <rect x="58" y="52" width="4" height="18" fill="#6D4C41" />
    <line x1="60" y1="45" x2="70" y2="25" stroke="#5D4037" strokeWidth="2" />
    <line x1="70" y1="25" x2="72" y2="55" stroke="#9E9E9E" strokeWidth="1" />
  </BuildingWrapper>
));

// 60. Market Stall
export const MarketStallSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="45" width="60" height="25" fill="#8D6E63" />
    <polygon points="5,45 40,20 75,45" fill="#FF5722" />
    <polygon points="5,45 40,25 40,45" fill="#E64A19" />
    <rect x="15" y="50" width="12" height="8" fill="#FFEB3B" />
    <rect x="30" y="50" width="12" height="8" fill="#F44336" />
    <rect x="45" y="50" width="12" height="8" fill="#4CAF50" />
    <rect x="60" y="50" width="8" height="8" fill="#FF9800" />
  </BuildingWrapper>
));

// ==================== STAGE 4: TOWN (Levels 61-80) ====================

// 61. School
export const SchoolSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="30" width="64" height="42" fill="#FFCCBC" stroke="#E64A19" strokeWidth="2" />
    <polygon points="40,8 2,32 78,32" fill="#E64A19" />
    <rect x="32" y="48" width="16" height="24" fill="#5D4037" />
    <rect x="12" y="38" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <rect x="54" y="38" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <rect x="12" y="56" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <rect x="54" y="56" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <circle cx="40" cy="18" r="6" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 62. Library
export const LibrarySVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="25" width="60" height="47" fill="#ECEFF1" stroke="#607D8B" strokeWidth="2" />
    <rect x="8" y="20" width="64" height="8" fill="#78909C" />
    <rect x="15" y="15" width="50" height="8" fill="#607D8B" />
    <rect x="32" y="50" width="16" height="22" fill="#5D4037" />
    <rect x="14" y="32" width="10" height="20" fill="#1565C0" />
    <rect x="26" y="32" width="10" height="20" fill="#C62828" />
    <rect x="44" y="32" width="10" height="20" fill="#2E7D32" />
    <rect x="56" y="32" width="10" height="20" fill="#F9A825" />
  </BuildingWrapper>
));

// 63. Town Hall
export const TownHallSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="35" width="64" height="37" fill="#ECEFF1" stroke="#78909C" strokeWidth="2" />
    <rect x="5" y="28" width="70" height="10" fill="#90A4AE" />
    <polygon points="40,5 0,30 80,30" fill="#607D8B" />
    <rect x="15" y="40" width="6" height="25" fill="#B0BEC5" />
    <rect x="28" y="40" width="6" height="25" fill="#B0BEC5" />
    <rect x="46" y="40" width="6" height="25" fill="#B0BEC5" />
    <rect x="59" y="40" width="6" height="25" fill="#B0BEC5" />
    <rect x="34" y="52" width="12" height="20" fill="#5D4037" />
    <circle cx="40" cy="15" r="5" fill="#FFC107" />
  </BuildingWrapper>
));

// 64. Grand Fountain
export const GrandFountainSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="68" rx="35" ry="8" fill="#607D8B" />
    <ellipse cx="40" cy="65" rx="30" ry="6" fill="#0288D1" />
    <ellipse cx="40" cy="62" rx="25" ry="4" fill="#03A9F4" />
    <rect x="36" y="35" width="8" height="28" fill="#78909C" />
    <ellipse cx="40" cy="38" rx="14" ry="5" fill="#90A4AE" />
    <ellipse cx="40" cy="25" rx="8" ry="18" fill="#4FC3F7" opacity="0.5" />
    <circle cx="40" cy="12" r="5" fill="#4FC3F7" opacity="0.3" />
    <circle cx="32" cy="20" r="3" fill="#4FC3F7" opacity="0.3" />
    <circle cx="48" cy="18" r="3" fill="#4FC3F7" opacity="0.3" />
  </BuildingWrapper>
));

// 65. Stone Bridge
export const StoneBridgeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="55" width="70" height="20" fill="#0288D1" />
    <path d="M 5 48 Q 25 35 40 35 Q 55 35 75 48" stroke="#78909C" strokeWidth="12" fill="none" />
    <path d="M 5 48 Q 25 38 40 38 Q 55 38 75 48" stroke="#90A4AE" strokeWidth="8" fill="none" />
    <rect x="8" y="38" width="8" height="18" fill="#607D8B" />
    <rect x="64" y="38" width="8" height="18" fill="#607D8B" />
    <rect x="8" y="35" width="8" height="5" fill="#546E7A" />
    <rect x="64" y="35" width="8" height="5" fill="#546E7A" />
  </BuildingWrapper>
));

// 66. Fire Station
export const FireStationSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="30" width="64" height="42" fill="#C62828" stroke="#B71C1C" strokeWidth="2" />
    <rect x="5" y="25" width="70" height="8" fill="#D32F2F" />
    <rect x="15" y="45" width="24" height="27" fill="#424242" />
    <rect x="41" y="45" width="24" height="27" fill="#424242" />
    <rect x="17" y="47" width="20" height="23" fill="#616161" />
    <rect x="43" y="47" width="20" height="23" fill="#616161" />
    <rect x="60" y="10" width="8" height="18" fill="#757575" />
    <circle cx="40" cy="38" r="5" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 67. Police Station
export const PoliceStationSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="30" width="64" height="42" fill="#1565C0" stroke="#0D47A1" strokeWidth="2" />
    <rect x="5" y="25" width="70" height="8" fill="#1976D2" />
    <rect x="32" y="48" width="16" height="24" fill="#5D4037" />
    <rect x="12" y="38" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <rect x="54" y="38" width="14" height="14" fill="#81D4FA" stroke="#5D4037" />
    <circle cx="40" cy="38" r="6" fill="#FFEB3B" />
    <polygon points="40,32 37,38 43,38" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 68. Hospital
export const HospitalSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="25" width="64" height="47" fill="#ECEFF1" stroke="#90A4AE" strokeWidth="2" />
    <rect x="5" y="20" width="70" height="8" fill="#F44336" />
    <rect x="32" y="52" width="16" height="20" fill="#5D4037" />
    <rect x="36" y="28" width="8" height="18" fill="#F44336" />
    <rect x="32" y="32" width="16" height="10" fill="#F44336" />
    <rect x="12" y="35" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="56" y="35" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="12" y="52" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="56" y="52" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
  </BuildingWrapper>
));

// 69. Post Office
export const PostOfficeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="32" width="60" height="40" fill="#FFF9C4" stroke="#F9A825" strokeWidth="2" />
    <polygon points="40,12 5,35 75,35" fill="#F44336" />
    <rect x="32" y="50" width="16" height="22" fill="#5D4037" />
    <rect x="15" y="40" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="53" y="40" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="55" y="58" width="10" height="12" fill="#F44336" />
    <rect x="57" y="62" width="6" height="4" fill="#212121" />
  </BuildingWrapper>
));

// 70. Bank
export const BankSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="32" width="64" height="40" fill="#ECEFF1" stroke="#607D8B" strokeWidth="2" />
    <rect x="5" y="25" width="70" height="10" fill="#78909C" />
    <polygon points="40,8 0,28 80,28" fill="#546E7A" />
    <rect x="14" y="38" width="8" height="28" fill="#90A4AE" />
    <rect x="28" y="38" width="8" height="28" fill="#90A4AE" />
    <rect x="44" y="38" width="8" height="28" fill="#90A4AE" />
    <rect x="58" y="38" width="8" height="28" fill="#90A4AE" />
    <rect x="34" y="52" width="12" height="20" fill="#5D4037" />
    <circle cx="40" cy="18" r="6" fill="#FFC107" />
  </BuildingWrapper>
));

// 71. Restaurant
export const RestaurantSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="30" width="60" height="42" fill="#FFCCBC" stroke="#E64A19" strokeWidth="2" />
    <polygon points="40,10 5,33 75,33" fill="#FF5722" />
    <rect x="28" y="50" width="24" height="22" fill="#5D4037" />
    <rect x="30" y="52" width="8" height="18" fill="#4E342E" />
    <rect x="42" y="52" width="8" height="18" fill="#4E342E" />
    <rect x="15" y="38" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="53" y="38" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <circle cx="40" cy="20" r="6" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 72. Cinema
export const CinemaSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="28" width="64" height="44" fill="#212121" stroke="#424242" strokeWidth="2" />
    <rect x="5" y="22" width="70" height="10" fill="#F44336" />
    <rect x="25" y="48" width="30" height="24" fill="#5D4037" />
    <rect x="28" y="50" width="10" height="20" fill="#4E342E" />
    <rect x="42" y="50" width="10" height="20" fill="#4E342E" />
    <rect x="15" y="35" width="18" height="10" fill="#FFD54F" />
    <rect x="47" y="35" width="18" height="10" fill="#FFD54F" />
    <text x="40" y="42" textAnchor="middle" fontSize="6" fill="#212121">NOW SHOWING</text>
  </BuildingWrapper>
));

// 73. Swimming Pool
export const SwimmingPoolSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="35" width="64" height="35" rx="3" fill="#90A4AE" />
    <rect x="12" y="40" width="56" height="25" rx="2" fill="#0288D1" />
    <rect x="15" y="43" width="50" height="19" fill="#03A9F4" />
    <ellipse cx="30" cy="52" rx="10" ry="4" fill="#4FC3F7" opacity="0.5" />
    <ellipse cx="50" cy="55" rx="8" ry="3" fill="#4FC3F7" opacity="0.5" />
    <rect x="60" y="28" width="8" height="15" fill="#78909C" />
    <rect x="12" y="28" width="8" height="15" fill="#78909C" />
  </BuildingWrapper>
));

// 74. Sports Pitch
export const SportsPitchSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="35" width="70" height="35" fill="#4CAF50" />
    <rect x="8" y="38" width="64" height="29" stroke="#ECEFF1" strokeWidth="2" fill="none" />
    <line x1="40" y1="38" x2="40" y2="67" stroke="#ECEFF1" strokeWidth="2" />
    <circle cx="40" cy="52" r="10" stroke="#ECEFF1" strokeWidth="2" fill="none" />
    <rect x="8" y="45" width="8" height="14" stroke="#ECEFF1" strokeWidth="2" fill="none" />
    <rect x="64" y="45" width="8" height="14" stroke="#ECEFF1" strokeWidth="2" fill="none" />
  </BuildingWrapper>
));

// 75. Bus Stop
export const BusStopSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="35" y="30" width="6" height="42" fill="#455A64" />
    <rect x="20" y="25" width="40" height="20" fill="#78909C" />
    <rect x="23" y="28" width="34" height="14" fill="#B0BEC5" />
    <rect x="38" y="70" width="20" height="4" fill="#616161" />
    <rect x="40" y="65" width="16" height="8" fill="#FFC107" />
    <text x="40" y="38" textAnchor="middle" fontSize="6" fill="#212121">BUS</text>
  </BuildingWrapper>
));

// 76. Car Park
export const CarParkSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="40" width="70" height="30" fill="#616161" />
    <rect x="10" y="45" width="20" height="10" fill="#FFEB3B" />
    <rect x="35" y="45" width="20" height="10" fill="#FFEB3B" />
    <rect x="22" y="58" width="16" height="10" fill="#2196F3" />
    <rect x="42" y="58" width="16" height="10" fill="#F44336" />
    <circle cx="40" cy="30" r="12" fill="#1565C0" />
    <text x="40" y="35" textAnchor="middle" fontSize="14" fill="#ECEFF1" fontWeight="bold">P</text>
  </BuildingWrapper>
));

// 77. Pet Shop
export const PetShopSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="32" width="60" height="40" fill="#E8F5E9" stroke="#4CAF50" strokeWidth="2" />
    <polygon points="40,12 5,35 75,35" fill="#66BB6A" />
    <rect x="30" y="50" width="20" height="22" fill="#5D4037" />
    <rect x="15" y="40" width="12" height="14" fill="#81D4FA" stroke="#5D4037" />
    <rect x="53" y="40" width="12" height="14" fill="#81D4FA" stroke="#5D4037" />
    <circle cx="21" cy="47" r="4" fill="#8D6E63" />
    <circle cx="59" cy="47" r="3" fill="#FF9800" />
  </BuildingWrapper>
));

// 78. Toy Shop
export const ToyShopSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="32" width="60" height="40" fill="#E1F5FE" stroke="#03A9F4" strokeWidth="2" />
    <polygon points="40,12 5,35 75,35" fill="#FF4081" />
    <rect x="28" y="50" width="24" height="22" fill="#5D4037" />
    <rect x="15" y="40" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="53" y="40" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <circle cx="21" cy="47" r="5" fill="#F44336" />
    <rect x="56" y="44" width="6" height="6" fill="#4CAF50" />
  </BuildingWrapper>
));

// 79. Music Hall
export const MusicHallSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="30" width="60" height="42" fill="#4A148C" stroke="#311B92" strokeWidth="2" />
    <polygon points="40,10 5,33 75,33" fill="#7B1FA2" />
    <rect x="28" y="50" width="24" height="22" fill="#5D4037" />
    <rect x="15" y="38" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <rect x="53" y="38" width="12" height="14" fill="#FFEB3B" stroke="#5D4037" />
    <ellipse cx="40" cy="20" rx="8" ry="5" fill="#FFC107" />
    <rect x="38" y="20" width="4" height="8" fill="#5D4037" />
  </BuildingWrapper>
));

// 80. Art Gallery
export const ArtGallerySVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="28" width="64" height="44" fill="#ECEFF1" stroke="#78909C" strokeWidth="2" />
    <rect x="5" y="22" width="70" height="10" fill="#607D8B" />
    <rect x="32" y="52" width="16" height="20" fill="#5D4037" />
    <rect x="14" y="35" width="14" height="14" fill="#E91E63" stroke="#5D4037" />
    <rect x="33" y="35" width="14" height="14" fill="#4CAF50" stroke="#5D4037" />
    <rect x="52" y="35" width="14" height="14" fill="#2196F3" stroke="#5D4037" />
  </BuildingWrapper>
));

// ==================== STAGE 5: CITY (Levels 81-100) ====================

// 81. Office Tower
export const OfficeTowerSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="20" y="15" width="40" height="57" fill="#607D8B" stroke="#455A64" strokeWidth="2" />
    <rect x="18" y="10" width="44" height="8" fill="#546E7A" />
    {[0, 1, 2, 3, 4].map(row => (
      [0, 1, 2].map(col => (
        <rect key={`${row}-${col}`} x={25 + col * 12} y={20 + row * 11} width="8" height="8" fill="#81D4FA" />
      ))
    ))}
    <rect x="32" y="58" width="16" height="14" fill="#455A64" />
  </BuildingWrapper>
));

// 82. Shopping Centre
export const ShoppingCentreSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="28" width="70" height="44" fill="#FFECB3" stroke="#FFA000" strokeWidth="2" />
    <rect x="2" y="22" width="76" height="10" fill="#FF8F00" />
    <rect x="18" y="50" width="18" height="22" fill="#5D4037" />
    <rect x="44" y="50" width="18" height="22" fill="#5D4037" />
    <rect x="8" y="35" width="14" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="58" y="35" width="14" height="12" fill="#81D4FA" stroke="#5D4037" />
    <text x="40" y="46" textAnchor="middle" fontSize="6" fill="#5D4037">MALL</text>
  </BuildingWrapper>
));

// 83. Monument
export const MonumentSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="28" y="60" width="24" height="12" fill="#78909C" />
    <rect x="32" y="50" width="16" height="12" fill="#90A4AE" />
    <polygon points="40,8 32,50 48,50" fill="#B0BEC5" />
    <polygon points="40,8 32,50 40,50" fill="#CFD8DC" />
    <rect x="38" y="5" width="4" height="8" fill="#FFC107" />
  </BuildingWrapper>
));

// 84. Stadium
export const StadiumSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="55" rx="35" ry="18" fill="#78909C" />
    <ellipse cx="40" cy="50" rx="30" ry="14" fill="#4CAF50" />
    <ellipse cx="40" cy="48" rx="25" ry="10" fill="#66BB6A" />
    <path d="M 10 40 Q 10 25 40 25 Q 70 25 70 40" stroke="#607D8B" strokeWidth="6" fill="none" />
    <rect x="8" y="38" width="8" height="20" fill="#90A4AE" />
    <rect x="64" y="38" width="8" height="20" fill="#90A4AE" />
    <rect x="36" y="20" width="8" height="10" fill="#FFEB3B" />
  </BuildingWrapper>
));

// 85. City Gate
export const CityGateSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="20" height="37" fill="#8D6E63" />
    <rect x="50" y="35" width="20" height="37" fill="#8D6E63" />
    <polygon points="20,35 20,20 15,18 25,18" fill="#6D4C41" />
    <polygon points="60,35 60,20 55,18 65,18" fill="#6D4C41" />
    <path d="M 30 72 L 30 45 Q 40 35 50 45 L 50 72" fill="#5D4037" />
    <rect x="10" y="32" width="60" height="6" fill="#795548" />
  </BuildingWrapper>
));

// 86. Airport
export const AirportSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="40" width="70" height="32" fill="#ECEFF1" stroke="#90A4AE" strokeWidth="2" />
    <rect x="2" y="35" width="76" height="8" fill="#607D8B" />
    <rect x="30" y="55" width="20" height="17" fill="#5D4037" />
    <rect x="10" y="48" width="15" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="55" y="48" width="15" height="12" fill="#81D4FA" stroke="#5D4037" />
    <polygon points="40,10 25,28 55,28" fill="#2196F3" />
    <rect x="38" y="15" width="4" height="25" fill="#1976D2" />
    <polygon points="40,5 35,15 45,15" fill="#1565C0" />
  </BuildingWrapper>
));

// 87. Railway Station
export const RailwayStationSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="35" width="64" height="37" fill="#BCAAA4" stroke="#8D6E63" strokeWidth="2" />
    <path d="M 8 35 Q 40 15 72 35" fill="#6D4C41" />
    <rect x="28" y="50" width="24" height="22" fill="#5D4037" />
    <circle cx="40" cy="30" r="10" fill="#FFEB3B" stroke="#5D4037" strokeWidth="2" />
    <line x1="40" y1="30" x2="40" y2="24" stroke="#212121" strokeWidth="2" />
    <line x1="40" y1="30" x2="46" y2="32" stroke="#212121" strokeWidth="2" />
    <rect x="12" y="42" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
    <rect x="56" y="42" width="12" height="12" fill="#81D4FA" stroke="#5D4037" />
  </BuildingWrapper>
));

// 88. University
export const UniversitySVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="32" width="64" height="40" fill="#D7CCC8" stroke="#8D6E63" strokeWidth="2" />
    <rect x="5" y="25" width="70" height="10" fill="#A1887F" />
    <polygon points="40,5 0,28 80,28" fill="#8D6E63" />
    <rect x="12" y="38" width="8" height="28" fill="#BCAAA4" />
    <rect x="24" y="38" width="8" height="28" fill="#BCAAA4" />
    <rect x="48" y="38" width="8" height="28" fill="#BCAAA4" />
    <rect x="60" y="38" width="8" height="28" fill="#BCAAA4" />
    <rect x="34" y="52" width="12" height="20" fill="#5D4037" />
    <polygon points="40,12 35,20 45,20" fill="#FFC107" />
  </BuildingWrapper>
));

// 89. Museum
export const MuseumSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="35" width="60" height="37" fill="#ECEFF1" stroke="#78909C" strokeWidth="2" />
    <rect x="7" y="28" width="66" height="10" fill="#90A4AE" />
    <polygon points="40,8 2,30 78,30" fill="#78909C" />
    <rect x="15" y="40" width="8" height="25" fill="#B0BEC5" />
    <rect x="30" y="40" width="8" height="25" fill="#B0BEC5" />
    <rect x="42" y="40" width="8" height="25" fill="#B0BEC5" />
    <rect x="57" y="40" width="8" height="25" fill="#B0BEC5" />
    <rect x="34" y="52" width="12" height="20" fill="#5D4037" />
    <text x="40" y="22" textAnchor="middle" fontSize="6" fill="#455A64">MUSEUM</text>
  </BuildingWrapper>
));

// 90. Theatre
export const TheatreSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="30" width="60" height="42" fill="#880E4F" stroke="#4A148C" strokeWidth="2" />
    <polygon points="40,8 5,33 75,33" fill="#AD1457" />
    <rect x="28" y="50" width="24" height="22" fill="#5D4037" />
    <rect x="30" y="52" width="8" height="18" fill="#3E2723" />
    <rect x="42" y="52" width="8" height="18" fill="#3E2723" />
    <circle cx="28" cy="42" r="8" fill="#FFC107" />
    <circle cx="52" cy="42" r="8" fill="#FFC107" />
    <path d="M 23 40 Q 28 45 33 40" stroke="#5D4037" strokeWidth="2" fill="none" />
    <path d="M 47 44 Q 52 39 57 44" stroke="#5D4037" strokeWidth="2" fill="none" />
  </BuildingWrapper>
));

// 91. Skyscraper
export const SkyscraperSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="25" y="10" width="30" height="62" fill="#455A64" stroke="#37474F" strokeWidth="2" />
    <rect x="23" y="5" width="34" height="8" fill="#37474F" />
    <polygon points="40,2 28,8 52,8" fill="#263238" />
    {[0, 1, 2, 3, 4, 5, 6].map(row => (
      [0, 1, 2].map(col => (
        <rect key={`${row}-${col}`} x={28 + col * 9} y={15 + row * 8} width="6" height="5" fill="#81D4FA" />
      ))
    ))}
    <rect x="35" y="60" width="10" height="12" fill="#546E7A" />
  </BuildingWrapper>
));

// 92. Radio Tower
export const RadioTowerSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <polygon points="40,5 28,72 52,72" fill="#78909C" />
    <polygon points="40,5 28,72 40,72" fill="#90A4AE" />
    <line x1="30" y1="25" x2="50" y2="25" stroke="#607D8B" strokeWidth="2" />
    <line x1="32" y1="40" x2="48" y2="40" stroke="#607D8B" strokeWidth="2" />
    <line x1="34" y1="55" x2="46" y2="55" stroke="#607D8B" strokeWidth="2" />
    <circle cx="40" cy="5" r="4" fill="#F44336" />
    <circle cx="40" cy="5" r="2" fill="#FF8A80" />
  </BuildingWrapper>
));

// 93. Lighthouse
export const LighthouseSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <ellipse cx="40" cy="70" rx="20" ry="5" fill="#78909C" />
    <polygon points="40,20 28,70 52,70" fill="#ECEFF1" />
    <polygon points="40,20 28,70 40,70" fill="#F44336" />
    <rect x="32" y="12" width="16" height="12" fill="#FFEB3B" />
    <polygon points="40,8 30,15 50,15" fill="#B71C1C" />
    <ellipse cx="40" cy="10" rx="15" ry="6" fill="#FFEB3B" opacity="0.4" />
  </BuildingWrapper>
));

// 94. Big Wheel (Ferris Wheel)
export const BigWheelSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <polygon points="40,70 30,70 40,35" fill="#607D8B" />
    <polygon points="40,70 50,70 40,35" fill="#78909C" />
    <circle cx="40" cy="35" r="28" stroke="#F44336" strokeWidth="4" fill="none" />
    <circle cx="40" cy="35" r="4" fill="#455A64" />
    <line x1="40" y1="7" x2="40" y2="63" stroke="#FFC107" strokeWidth="2" />
    <line x1="12" y1="35" x2="68" y2="35" stroke="#FFC107" strokeWidth="2" />
    <line x1="20" y1="15" x2="60" y2="55" stroke="#FFC107" strokeWidth="2" />
    <line x1="60" y1="15" x2="20" y2="55" stroke="#FFC107" strokeWidth="2" />
    <circle cx="40" cy="7" r="4" fill="#2196F3" />
    <circle cx="40" cy="63" r="4" fill="#4CAF50" />
    <circle cx="12" cy="35" r="4" fill="#FF9800" />
    <circle cx="68" cy="35" r="4" fill="#9C27B0" />
  </BuildingWrapper>
));

// 95. Roller Coaster
export const RollerCoasterSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="60" width="70" height="12" fill="#78909C" />
    <rect x="8" y="25" width="6" height="38" fill="#607D8B" />
    <rect x="66" y="45" width="6" height="18" fill="#607D8B" />
    <path d="M 11 25 Q 25 10 40 35 Q 55 60 69 45" stroke="#F44336" strokeWidth="4" fill="none" />
    <rect x="18" y="18" width="10" height="6" rx="2" fill="#2196F3" />
    <rect x="45" y="45" width="10" height="6" rx="2" fill="#4CAF50" />
  </BuildingWrapper>
));

// 96. Aquarium
export const AquariumSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="8" y="30" width="64" height="42" fill="#E1F5FE" stroke="#0288D1" strokeWidth="2" />
    <path d="M 8 30 Q 40 15 72 30" fill="#01579B" />
    <rect x="30" y="52" width="20" height="20" fill="#0277BD" />
    <ellipse cx="25" cy="45" r="8" fill="#FF9800" />
    <polygon points="17,45 12,40 12,50" fill="#FF9800" />
    <ellipse cx="55" cy="50" r="6" fill="#4CAF50" />
    <polygon points="49,50 44,46 44,54" fill="#4CAF50" />
    <circle cx="45" cy="38" r="3" fill="#03A9F4" />
    <circle cx="35" cy="42" r="2" fill="#03A9F4" />
    <circle cx="60" cy="40" r="2" fill="#03A9F4" />
  </BuildingWrapper>
));

// 97. Zoo
export const ZooSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="35" width="70" height="37" fill="#A5D6A7" stroke="#4CAF50" strokeWidth="2" />
    <rect x="30" y="20" width="20" height="18" fill="#8D6E63" />
    <polygon points="40,10 25,22 55,22" fill="#6D4C41" />
    <rect x="36" y="50" width="8" height="22" fill="#5D4037" />
    <ellipse cx="20" cy="55" r="10" fill="#FFC107" />
    <circle cx="17" cy="52" r="2" fill="#3E2723" />
    <ellipse cx="60" cy="55" r="8" fill="#795548" />
    <circle cx="58" cy="52" r="2" fill="#3E2723" />
    <text x="40" y="32" textAnchor="middle" fontSize="6" fill="#5D4037">ZOO</text>
  </BuildingWrapper>
));

// 98. Space Centre
export const SpaceCentreSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="10" y="40" width="40" height="32" fill="#ECEFF1" stroke="#78909C" strokeWidth="2" />
    <rect x="7" y="35" width="46" height="8" fill="#607D8B" />
    <polygon points="60,72 50,72 55,20 65,20" fill="#B0BEC5" />
    <polygon points="55,20 52,8 68,8 65,20" fill="#F44336" />
    <ellipse cx="60" cy="68" rx="12" ry="6" fill="#FF6F00" opacity="0.7" />
    <rect x="20" y="48" width="15" height="12" fill="#81D4FA" stroke="#5D4037" />
    <circle cx="70" cy="25" r="5" fill="#FFEB3B" opacity="0.6" />
  </BuildingWrapper>
));

// 99. Castle
export const CastleSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="15" y="35" width="50" height="37" fill="#9E9E9E" stroke="#757575" strokeWidth="2" />
    <rect x="10" y="25" width="15" height="15" fill="#757575" />
    <rect x="55" y="25" width="15" height="15" fill="#757575" />
    <rect x="8" y="20" width="6" height="8" fill="#616161" />
    <rect x="16" y="20" width="6" height="8" fill="#616161" />
    <rect x="58" y="20" width="6" height="8" fill="#616161" />
    <rect x="66" y="20" width="6" height="8" fill="#616161" />
    <rect x="34" y="15" width="12" height="12" fill="#757575" />
    <rect x="32" y="10" width="6" height="8" fill="#616161" />
    <rect x="42" y="10" width="6" height="8" fill="#616161" />
    <path d="M 35 72 L 35 52 Q 40 45 45 52 L 45 72" fill="#5D4037" />
    <rect x="20" y="45" width="8" height="10" fill="#455A64" />
    <rect x="52" y="45" width="8" height="10" fill="#455A64" />
  </BuildingWrapper>
));

// 100. Golden Bridge
export const GoldenBridgeSVG = memo(({ scale = 1, isUserBuilding }) => (
  <BuildingWrapper scale={scale} isUserBuilding={isUserBuilding}>
    <rect x="5" y="55" width="70" height="18" fill="#0288D1" />
    <rect x="5" y="48" width="70" height="10" fill="#FF6F00" />
    <rect x="15" y="25" width="8" height="28" fill="#E65100" />
    <rect x="57" y="25" width="8" height="28" fill="#E65100" />
    <rect x="13" y="20" width="12" height="8" fill="#FF8F00" />
    <rect x="55" y="20" width="12" height="8" fill="#FF8F00" />
    <line x1="19" y1="20" x2="5" y2="48" stroke="#FFB300" strokeWidth="2" />
    <line x1="19" y1="20" x2="61" y2="20" stroke="#FFB300" strokeWidth="3" />
    <line x1="61" y1="20" x2="75" y2="48" stroke="#FFB300" strokeWidth="2" />
    <line x1="19" y1="20" x2="30" y2="48" stroke="#FFB300" strokeWidth="1" />
    <line x1="61" y1="20" x2="50" y2="48" stroke="#FFB300" strokeWidth="1" />
  </BuildingWrapper>
));

// ==================== EXPORT MAPPING ====================

// Map building IDs (levels 1-100) to their SVG components
export const BUILDING_SVG_MAP = {
  // Stage 1 - Campsite (1-20)
  1: TentSVG,
  2: CampfireSVG,
  3: PineTreeSVG,
  4: OakTreeSVG,
  5: BushSVG,
  6: FlowerBedSVG,
  7: RockSVG,
  8: LogBenchSVG,
  9: LanternSVG,
  10: ToadstoolSVG,
  11: BirdBoxSVG,
  12: BeehiveSVG,
  13: PondSVG,
  14: WoodenSignSVG,
  15: HammockSVG,
  16: FireRingSVG,
  17: PicnicTableSVG,
  18: CoolBoxSVG,
  19: FishingRodSVG,
  20: CanoeSVG,
  
  // Stage 2 - Hamlet (21-40)
  21: CottageSVG,
  22: WellSVG,
  23: VeggiePatchSVG,
  24: WoodenFenceSVG,
  25: CobblePathSVG,
  26: ChickenCoopSVG,
  27: WindmillSVG,
  28: HayBaleSVG,
  29: ScarecrowSVG,
  30: AppleTreeSVG,
  31: SunflowersSVG,
  32: StoneWallSVG,
  33: TrolleySVG,
  34: BarnSVG,
  35: SheepPenSVG,
  36: HorseStableSVG,
  37: PostBoxSVG,
  38: SignpostSVG,
  39: BreadOvenSVG,
  40: WaterTroughSVG,
  
  // Stage 3 - Village (41-60)
  41: HouseSVG,
  42: ShopSVG,
  43: ChapelSVG,
  44: VillageGreenSVG,
  45: PavedRoadSVG,
  46: StreetLampSVG,
  47: ParkBenchVillageSVG,
  48: NoticeBoardSVG,
  49: BakerySVG,
  50: BlacksmithSVG,
  51: InnSVG,
  52: FountainSVG,
  53: ClockTowerSVG,
  54: FlowerStallSVG,
  55: BandstandSVG,
  56: PlaygroundSVG,
  57: StatueSVG,
  58: BridgeSVG,
  59: FishingPierSVG,
  60: MarketStallSVG,
  
  // Stage 4 - Town (61-80)
  61: SchoolSVG,
  62: LibrarySVG,
  63: TownHallSVG,
  64: GrandFountainSVG,
  65: StoneBridgeSVG,
  66: FireStationSVG,
  67: PoliceStationSVG,
  68: HospitalSVG,
  69: PostOfficeSVG,
  70: BankSVG,
  71: RestaurantSVG,
  72: CinemaSVG,
  73: SwimmingPoolSVG,
  74: SportsPitchSVG,
  75: BusStopSVG,
  76: CarParkSVG,
  77: PetShopSVG,
  78: ToyShopSVG,
  79: MusicHallSVG,
  80: ArtGallerySVG,
  
  // Stage 5 - City (81-100)
  81: OfficeTowerSVG,
  82: ShoppingCentreSVG,
  83: MonumentSVG,
  84: StadiumSVG,
  85: CityGateSVG,
  86: AirportSVG,
  87: RailwayStationSVG,
  88: UniversitySVG,
  89: MuseumSVG,
  90: TheatreSVG,
  91: SkyscraperSVG,
  92: RadioTowerSVG,
  93: LighthouseSVG,
  94: BigWheelSVG,
  95: RollerCoasterSVG,
  96: AquariumSVG,
  97: ZooSVG,
  98: SpaceCentreSVG,
  99: CastleSVG,
  100: GoldenBridgeSVG,
};

// Helper function to get building SVG by level/ID
export const getBuildingSVG = (levelOrId) => {
  return BUILDING_SVG_MAP[levelOrId] || null;
};

// Building names for reference (UK English)
export const BUILDING_NAMES = {
  1: "Tent", 2: "Campfire", 3: "Pine Tree", 4: "Oak Tree", 5: "Bush",
  6: "Flower Bed", 7: "Rock", 8: "Log Bench", 9: "Lantern", 10: "Toadstool",
  11: "Bird Box", 12: "Beehive", 13: "Pond", 14: "Wooden Sign", 15: "Hammock",
  16: "Fire Ring", 17: "Picnic Table", 18: "Cool Box", 19: "Fishing Rod", 20: "Canoe",
  21: "Cottage", 22: "Well", 23: "Veggie Patch", 24: "Wooden Fence", 25: "Cobble Path",
  26: "Chicken Coop", 27: "Windmill", 28: "Hay Bale", 29: "Scarecrow", 30: "Apple Tree",
  31: "Sunflowers", 32: "Stone Wall", 33: "Trolley", 34: "Barn", 35: "Sheep Pen",
  36: "Horse Stable", 37: "Post Box", 38: "Signpost", 39: "Bread Oven", 40: "Water Trough",
  41: "House", 42: "Shop", 43: "Chapel", 44: "Village Green", 45: "Paved Road",
  46: "Street Lamp", 47: "Park Bench", 48: "Notice Board", 49: "Bakery", 50: "Blacksmith",
  51: "Inn", 52: "Fountain", 53: "Clock Tower", 54: "Flower Stall", 55: "Bandstand",
  56: "Playground", 57: "Statue", 58: "Bridge", 59: "Fishing Pier", 60: "Market Stall",
  61: "School", 62: "Library", 63: "Town Hall", 64: "Grand Fountain", 65: "Stone Bridge",
  66: "Fire Station", 67: "Police Station", 68: "Hospital", 69: "Post Office", 70: "Bank",
  71: "Restaurant", 72: "Cinema", 73: "Swimming Pool", 74: "Sports Pitch", 75: "Bus Stop",
  76: "Car Park", 77: "Pet Shop", 78: "Toy Shop", 79: "Music Hall", 80: "Art Gallery",
  81: "Office Tower", 82: "Shopping Centre", 83: "Monument", 84: "Stadium", 85: "City Gate",
  86: "Airport", 87: "Railway Station", 88: "University", 89: "Museum", 90: "Theatre",
  91: "Skyscraper", 92: "Radio Tower", 93: "Lighthouse", 94: "Big Wheel", 95: "Roller Coaster",
  96: "Aquarium", 97: "Zoo", 98: "Space Centre", 99: "Castle", 100: "Golden Bridge",
};

export default BUILDING_SVG_MAP;
